import React, { useCallback, useMemo } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { DropdownItem } from '@atlaskit/dropdown-menu';
import UploadIcon from '@atlaskit/icon/core/upload';

import { ACTION_SLIDES_EXPORT, ACTION_SLIDES_EXPORT_POWERPOINT } from '../moreActionsConstants.ts';

import {
	ExpandableNestedMenuItem,
	type ExpandableNestedMenuItemItemType,
} from './ExpandableNestedMenuItem.tsx';
import { i18n as sharedI18n, NestedMenuTypes } from './SharedActionMenuItems.tsx';

const i18n = defineMessages({
	slidesExportPowerPoint: {
		id: 'content-types-header.ellipsis.open.ellipsis.slides.export.powerpoint.non-final',
		defaultMessage: 'PowerPoint',
		description:
			'The text shown as a sub-menu item under the Export menu for slides content type, representing the PowerPoint export option.',
	},
});

type SlidesExportMenuItemProps = {
	handleNestedMenuItemClick: (type: NestedMenuTypes) => void;
	nestedPopup: NestedMenuTypes | undefined;
	handleNestedMenuItemClose: () => void;
	closeParentMenu: () => void;
};

/**
 * Slides-specific Export menu item. Renders a nested menu with a single
 * "PowerPoint" option that is a no-op for now. This will eventually be wired
 * up via the canvas bridge to drive real export functionality.
 */
export const SlidesExportMenuItem = ({
	handleNestedMenuItemClick,
	nestedPopup,
	handleNestedMenuItemClose,
	closeParentMenu,
}: SlidesExportMenuItemProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const intl = useIntl();

	const handlePowerPointClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'slidesExportPowerPoint',
				source: 'overflowMenu',
			},
		}).fire();
		// No-op for now. Will be wired to the canvas bridge in a follow-up.
		closeParentMenu();
	}, [createAnalyticsEvent, closeParentMenu]);

	const slidesExportItems: ExpandableNestedMenuItemItemType[] = useMemo(
		() => [
			{
				id: ACTION_SLIDES_EXPORT_POWERPOINT,
				label: intl.formatMessage(i18n.slidesExportPowerPoint),
				url: '',
				customComponent: () => (
					<DropdownItem
						key={ACTION_SLIDES_EXPORT_POWERPOINT}
						onClick={handlePowerPointClick}
						testId="slides-export-powerpoint"
					>
						<FormattedMessage {...i18n.slidesExportPowerPoint} />
					</DropdownItem>
				),
			},
			// TODO: Add PDF and Google Slides export options here.
		],
		[intl, handlePowerPointClick],
	);

	const handleSlidesExportMenuItemClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'overflowSlidesExport',
				source: 'overflowMenu',
			},
		}).fire();
		handleNestedMenuItemClick(NestedMenuTypes.Export);
	}, [createAnalyticsEvent, handleNestedMenuItemClick]);

	return (
		<ExpandableNestedMenuItem
			id="slides-export"
			label={<FormattedMessage {...sharedI18n.export} />}
			items={slidesExportItems}
			iconBefore={<UploadIcon label="" />}
			isNestedMenuOpen={nestedPopup === NestedMenuTypes.Export}
			onClick={handleSlidesExportMenuItemClick}
			key={ACTION_SLIDES_EXPORT}
			onMenuClose={handleNestedMenuItemClose}
		/>
	);
};
